import { useJwtManager } from "@getbread/wheat-jwt-manager/lib/useJwtManager";
import { createContext, useContext, useMemo, useState } from "react";
import { buyerId as mockBuyerId } from "../../api-client/mocks";
import type { FCWithChildren } from "../../api-client/resources/types/sharedTypes";
import { jwtStorageKey } from "../../apiClientProvider";
import { logger } from "../../logger";
import { isMirageEnabled } from "../../utils/importMeta";

export type AuthenticationContextValue = {
	loggedIn: boolean;
	logIn: (jwt: string) => void;
	logOut: () => void;
	buyerId: string;
	setMerchantName: (merchant: string) => void;
	selectedMerchant?: string;
};

const AuthenticationContext = createContext<AuthenticationContextValue | undefined>(undefined);

const AuthenticationProvider: FCWithChildren = ({ children }) => {
	const {
		payload: jwtPayload,
		setJwt,
		removeJwt,
	} = useJwtManager<{
		buyerID: string;
	}>({ storageKey: jwtStorageKey });

	// if we have a JWT, extract the buyer ID from that
	const buyerId: string | null = useMemo(
		() => (isMirageEnabled() ? mockBuyerId : (jwtPayload?.buyerID ?? null)),
		[jwtPayload],
	);

	const [selectedMerchant, setSelectedMerchant] = useState<string>();

	const setMerchantName = (merchant: string) => {
		setSelectedMerchant(merchant);
	};

	const contextValue = useMemo(() => {
		const logIn = (jwt: string) => {
			setJwt(jwt);
		};

		/** INTERNAL: Use useLogout() hook instead */
		const logOut = () => {
			removeJwt();
		};

		const loggedIn = !!jwtPayload;
		logger.addCommonProperties({ buyerId: loggedIn ? buyerId : null });

		return {
			loggedIn,
			logIn,
			/** INTERNAL: Use useLogout() hook instead */
			logOut,
			buyerId: buyerId!,
			setMerchantName,
			selectedMerchant,
		};
	}, [jwtPayload, buyerId, selectedMerchant, setJwt, removeJwt]);

	return (
		<AuthenticationContext.Provider value={contextValue}>
			{children}
		</AuthenticationContext.Provider>
	);
};

function useAuthentication(): AuthenticationContextValue {
	const context = useContext(AuthenticationContext);

	if (!context) {
		throw new Error("useAuthentication must be used within a context");
	}

	return context;
}

export { AuthenticationProvider, useAuthentication };
